import { AxiosError, AxiosResponse } from 'axios';
import { PolicyValidatorErrors } from '.';
import { IGenericError, IErrorReturnService } from '../models/errors';
import {
  AprissFinalizeRequest,
  FraudDecision,
  IScheduledPickupsRequest,
  IScheduledPickupsResponse,
  ICreateReturnRequestBody,
  ICreateReturnResponse,
  Identifier,
  IRefundRequest,
  IRefundResponse,
  IShipmentType,
  ISorisFinalizeRequest,
  ISorisFinalizeResponse,
  IValidateAddressRequest,
  IValidateAddressResponse,
  IValidateZipCodeRequest,
  IValidateZipCodeResponse,
  OrderVersionRequest,
} from '../models/api';
import { RefundValues } from '../models/returns';
import {
  AprissFinalizeAPI,
  FinalizeSorisReturnAPI,
  OrderVersionAPI,
  ScheduledPickupsAPI,
  RefundEstimationAPI,
  SSReturnCreateAPI,
  ValidateAddressAPI,
  ValidateZipCodeAPI,
} from '../api/returns';
import {
  TagApiError,
  TagFinalizeReturnApiError,
  TagPostPageError,
} from './analytics-tagging';
import {
  emitReturnsOutcome,
  emitReturnsBusinessError,
} from './returns-instrumentation-context';
import { shipmentTypeToIdentifierMap } from '../constants/general';
import {
  LORP_SCHEDULE_PICKUP_ERROR_MESSAGE,
  RETURN_TECHNICAL_ERROR_WITH_ASSISTANCE,
} from '../constants/analytics';

/**
 * True for 5xx/network/timeout Axios failures (as opposed to a well-formed 4xx
 * error carrying a backend code). Shared so error-type mapping stays consistent.
 */
export const isNetworkError = (
  error?: AxiosError<IGenericError> | null,
): boolean => {
  const { response, code } = error || {};
  return (
    response?.status === 500 ||
    code === 'ERR_NETWORK' ||
    code === 'ECONNABORTED'
  );
};

export const getAnalyticsErrorType = (
  error: AxiosError<IGenericError>,
): 'system' | 'error' => (isNetworkError(error) ? 'system' : 'error');

const isAxiosLikeError = (error: unknown): error is AxiosError<IGenericError> =>
  typeof error === 'object' &&
  error !== null &&
  ('response' in error || 'code' in error);

/**
 * Safely checks the AxiosError for the Generic Error Model and returns the error data
 * @param error - The Axios error object.
 * @returns Returns the object inside the error (code, description and retryable) If none found, will return undefined
 */
export const getAPIErrorData = (
  error: AxiosError<IGenericError>,
): IErrorReturnService | undefined => {
  const errorData = error?.response?.data?.errors?.[0] || undefined;
  return errorData;
};

export const unknownError: IErrorReturnService = {
  code: 'UNKNOWN',
  description: 'Unknown Error',
  retryable: true,
};

/**
 * Error code surfaced when a request aborts on the client-side request timeout.
 * Unlike a server error, a timeout means the FE does NOT know whether the call
 * succeeded — callers (e.g. Keep It createReturn) use this to drive a retry UX
 * instead of treating it as a definitive failure.
 */
export const TIMEOUT_ERROR_CODE = 'TIMEOUT';

export const timeoutError: IErrorReturnService = {
  code: TIMEOUT_ERROR_CODE,
  description: 'Request timed out',
  retryable: true,
};

/**
 * Detects an Axios client-side timeout. Axios surfaces a timed-out request with
 * `code === 'ECONNABORTED'` and no `response` (the same signal `getErrorType`
 * and analytics-tagging key off of).
 */
export const isTimeoutError = (error: AxiosError<IGenericError>): boolean =>
  error?.code === 'ECONNABORTED';

/**
 * Checks if the given error is related to a multi-shipment address error.
 * This error should only occur during phase 1 of multi vendor address
 * @param error - The Axios error object.
 * @returns True if the error is a multi-shipment address error, false otherwise.
 */
export const isMultiShipmentAddressError = (
  error: AxiosError<IGenericError>,
) => {
  const errorCode = getAPIErrorData(error)?.code;
  return error?.response?.status === 400 && errorCode === 'MULTI_SHIPMENTS';
};

export const isProtectionPlanError = (error: AxiosError<IGenericError>) =>
  error?.status === 400 &&
  error?.response?.data?.errors?.[0]?.code === 'SSR_HRTVPOLICY_ERR_4102';

/**
 * Gets the PolicyValidator error type based on the API response.
 *
 * @param {AxiosError} error - The AxiosError object representing the HTTP error.
 * @returns {PolicyValidatorErrors} The type of error scenario that occurred.
 *
 * @description
 * This function checks the error message of Axios (created when we throw an error inside a Promise), while also checking
 * the error status code+body of the response to determine the appropriate scenario type.
 * If the error is a 5xx network error, it returns PolicyValidatorErrors.NETWORK.
 * If the error is related to multiple shipments (e.g., status 400 and specific error code),
 *  it returns PolicyValidatorErrors.MULTI_SHIPMENTS.
 * If HRTV validation contains some ineligible items, return PolicyValidatorErrors.INELIGIBLE_ITEMS
 * Otherwise, return PolicyValidatorErrors.GENERIC.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getErrorType = (error: AxiosError<IGenericError>) => {
  const { message } = error;
  const code = getAPIErrorData(error)?.code;
  if (message === 'HAS_INELIGIBLE_ITEMS') {
    return PolicyValidatorErrors.HAS_INELIGIBLE_ITEMS;
  }
  if (code === 'SSR_UPS_ERR_1002') {
    return PolicyValidatorErrors.SSR_UPS_ERR_1002;
  }

  if (isNetworkError(error)) return PolicyValidatorErrors.NETWORK;

  const isMultiShipmentError = isMultiShipmentAddressError(error);

  if (isMultiShipmentError) return PolicyValidatorErrors.MULTI_SHIPMENTS;

  return PolicyValidatorErrors.GENERIC;
};

export const fetchRefundEstimationAPI = (
  refundRequest: IRefundRequest,
): Promise<RefundValues | undefined> => {
  return RefundEstimationAPI(refundRequest)
    .then((response: AxiosResponse<IRefundResponse>) => {
      const { refund } = response.data;
      if (refund) {
        emitReturnsOutcome('refund_estimation', 'shown');
        return refund;
      }
      throw new Error('RefundEstimationAPI: Empty 2xx Response');
    })
    .catch((error: AxiosError<IGenericError>) => {
      TagApiError(error);
      // Business-silent: on failure/empty the summary hides the refund breakdown
      // with no visible error — broadcast so we can see how often customers finish blind.
      emitReturnsOutcome('refund_estimation', 'hidden');
      return undefined;
    });
};

export const handleAddressValidateCall = (request: IValidateAddressRequest) =>
  ValidateAddressAPI(request).then(
    (response: AxiosResponse<IValidateAddressResponse>) => {
      return response.data;
    },
  );

export const handleZipCodeValidateCall = (request: IValidateZipCodeRequest) =>
  ValidateZipCodeAPI(request).then(
    (response: AxiosResponse<IValidateZipCodeResponse>) => {
      return response.data;
    },
  );

export const handleMultiReturnCreateCall = (
  requestSsReturnCreate: ICreateReturnRequestBody,
) => {
  const temporaryRequest = {
    ...requestSsReturnCreate,
    order: { ...requestSsReturnCreate },
  };
  return SSReturnCreateAPI(temporaryRequest)
    .then((response: AxiosResponse<ICreateReturnResponse>) => {
      const returnSuccessData = response?.data?.returnService;
      if (
        !returnSuccessData ||
        returnSuccessData.status !== 'success' ||
        !returnSuccessData.ssrCOMReturnServiceResponse
      ) {
        throw new Error('No return data found');
      }
      return {
        data: returnSuccessData.ssrCOMReturnServiceResponse,
        error: null,
      };
    })
    .catch((error: AxiosError<IGenericError>) => {
      TagFinalizeReturnApiError(error);
      const apiError = getAPIErrorData(error);
      const errorCode = String(apiError?.code ?? '');
      // A timeout has no response body, so getAPIErrorData() would collapse it
      // into the generic unknownError. Surface it distinctly so the Keep It flow
      // can show its retry screen rather than falling back / failing outright.
      if (isTimeoutError(error)) {
        emitReturnsOutcome('create_return', 'timeout');
        return { error: timeoutError, data: null };
      }
      // Duplicate is treated as success by the caller (barebones summary); still a
      // distinct business outcome worth counting separately from a hard failure.
      const isDuplicate =
        errorCode === 'COM_RETURN_RECORD_ALREADY_EXISTS_ERROR';
      emitReturnsOutcome(
        'create_return',
        isDuplicate ? 'duplicate' : 'business_fail',
      );
      if (!isDuplicate) {
        emitReturnsBusinessError(errorCode || 'create_failed', {
          source: 'create_return',
        });
      }
      return { error: apiError || unknownError, data: null };
    });
};

export const handleScheduledPickupsCall = (
  requestScheduledPickups: IScheduledPickupsRequest,
) => {
  return ScheduledPickupsAPI(requestScheduledPickups)
    .then((response: AxiosResponse<IScheduledPickupsResponse>) => {
      const returnSuccessData = response?.data;
      if (!returnSuccessData || !returnSuccessData.pickupDates) {
        throw new Error('No scheduled pickups found');
      }
      return { data: returnSuccessData.pickupDates, error: null };
    })
    .catch((error: AxiosError<IGenericError>) => {
      TagPostPageError({
        type: getAnalyticsErrorType(error),
        pageSection: 'large item pickup schedule',
        message: LORP_SCHEDULE_PICKUP_ERROR_MESSAGE,
        code: getAPIErrorData(error)?.code,
      });
      return { error: getAPIErrorData(error) || unknownError, data: null };
    });
};

export const handleVersionIDCall = async (
  orderId: string,
  emailId: string,
  isGuest: boolean,
  net?: string,
): Promise<{
  versionId: string | null;
  error: IErrorReturnService | null;
}> => {
  try {
    const request: OrderVersionRequest = {
      orderId,
      emailId,
      isGuest,
      net,
    };
    const response = await OrderVersionAPI(request);
    if (!response.data?.orderVersion) {
      throw new Error('Version ID Fetch Error: No orderVersion found');
    }
    return { versionId: response.data.orderVersion, error: null };
  } catch (error) {
    if (isAxiosLikeError(error)) {
      const axiosError = error as AxiosError<IGenericError>;
      const errorData = getAPIErrorData(axiosError);
      TagPostPageError({
        type: getAnalyticsErrorType(axiosError),
        pageSection: 'return create',
        message: RETURN_TECHNICAL_ERROR_WITH_ASSISTANCE,
        code: errorData?.code,
      });
      return { error: errorData || unknownError, versionId: null };
    }
    TagPostPageError({
      pageSection: 'return create',
      message: RETURN_TECHNICAL_ERROR_WITH_ASSISTANCE,
    });
    return { error: unknownError, versionId: null };
  }
};

export const handleAprissCall = (request: AprissFinalizeRequest) => {
  return AprissFinalizeAPI(request).catch(() => {
    // Fraud/compliance finalize is fire-and-forget; broadcast the failure so it is
    // no longer completely invisible (this catch was previously empty).
    emitReturnsOutcome('apriss_finalize', 'error');
  });
};

export const handleSORISCreateCall = (request: ISorisFinalizeRequest) => {
  return FinalizeSorisReturnAPI(request)
    .then((response: AxiosResponse<ISorisFinalizeResponse>) => {
      const returnSuccessData = response?.data;
      if (!returnSuccessData) {
        throw new Error('No return data found');
      }
      if (returnSuccessData.sorisQRCode) {
        emitReturnsOutcome('soris_create', 'qr_generated');
      }
      return {
        data: returnSuccessData,
        error: null,
      };
    })
    .catch((error: AxiosError<IGenericError>) => {
      TagFinalizeReturnApiError(error);
      const apiError = getAPIErrorData(error);
      emitReturnsOutcome('soris_create', 'business_fail');
      emitReturnsBusinessError(
        String(apiError?.code ?? 'soris_create_failed'),
        {
          source: 'soris_create',
        },
      );
      return { error: apiError || unknownError, data: null };
    });
};

/**
 * Determines the identifier to use for API calls based on fraud decisions and shipment type.
 * If there is only one fraud decision, returns '0' regardless of the actual identifier.
 * For multiple fraud decisions, returns the identifier that matches the shipment type.
 */
export const getIdentifier = (
  fraudDecisions: FraudDecision[],
  shipmentType: string,
): Identifier | undefined => {
  // If there is only one fraud decision, return '0'
  if (fraudDecisions.length === 1) {
    return '0';
  }

  // Otherwise, match the shipment type to the identifier
  return fraudDecisions.find(
    (decision) =>
      decision.identifier ===
      shipmentTypeToIdentifierMap[shipmentType as IShipmentType],
  )?.identifier;
};
